import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ArrowUpRight, BarChart3, Bot, Building2, Check, Clock, Laptop, Plug, ShieldCheck, UserPlus, Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const HRFLARE = 'https://hrflare.com';

// Screenshots taken from the live product tour on hrflare.com.
const tour = [
  { key: 'dashboard', label: 'Dashboard', alt: 'HR FLARE dashboard with an AI copilot bar and live workforce stats' },
  { key: 'ai', label: 'AI Assistant', alt: 'HR FLARE AI assistant answering HR questions and drafting reports' },
  { key: 'core-hr', label: 'Core HR', alt: 'HR FLARE employee directory listing the team with departments and roles' },
  { key: 'analytics', label: 'Analytics', alt: 'HR FLARE people analytics dashboard showing headcount trends' },
  { key: 'recruitment', label: 'Recruitment', alt: 'HR FLARE recruitment pipeline with open requisitions and candidates' },
  { key: 'attendance', label: 'Attendance', alt: 'HR FLARE attendance page with check-in and weekly hours' },
  { key: 'leaves', label: 'Leaves', alt: 'HR FLARE leave management with balances and requests' },
  { key: 'org-chart', label: 'Org Chart', alt: 'HR FLARE org chart showing the company reporting structure' },
];
const shot = (key: string) => `/products/hrflare-live-${key}.webp`;

const features = [
  { icon: Bot, title: 'AI HR copilot', body: 'Ask anything in plain language and get structured answers with sources. It runs real actions: leave requests, announcements, reports.', wide: true },
  { icon: Plug, title: 'Bring your own AI', body: 'Connect Claude or ChatGPT over MCP with secure OAuth 2.1 sign-in, and manage HR from your chat window.', wide: true },
  { icon: Users, title: 'Core HR', body: 'Employees, departments, bulk import from CSV or Excel, and approvals that follow the org chart.' },
  { icon: Clock, title: 'Attendance & leave', body: 'Check-in, work locations, balances and approvals, with automated reporting.' },
  { icon: BarChart3, title: 'Analytics', body: 'Headcount, time off, expenses and compensation, scoped to each role.' },
  { icon: UserPlus, title: 'Recruitment', body: 'A visual hiring pipeline and public job pages where AI reads the CV and fills the form.' },
  { icon: Building2, title: 'Multi-company', body: 'Run every company from one login and switch from the top bar.' },
  { icon: Laptop, title: 'Mac app', body: 'Your hours in the menu bar, with clock in and out and notifications.' },
];

const trust = ['Free for up to 5 employees', 'English & Deutsch', 'GDPR-conscious', '50+ granular permissions'];

const Products = () => {
  const [active, setActive] = useState(tour[0].key);
  const current = tour.find(t => t.key === active) ?? tour[0];

  return (
    <div className="relative isolate min-h-screen overflow-hidden pt-16">
      <Helmet>
        <title>Products | HR FLARE AI HR Platform | Omnicraft Services</title>
        <meta
          name="description"
          content="HR FLARE is Omnicraft Services' own AI-powered HR platform: employees, hiring, attendance and payroll in one workspace, with an AI copilot that works from Claude and ChatGPT."
        />
        <link rel="icon" type="image/png" href="/logo.png" />
      </Helmet>

      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[760px]">
        <div className="absolute left-1/2 top-[-200px] h-[560px] w-[900px] max-w-full -translate-x-1/2 rounded-full bg-cyan-500/20 blur-[120px]" />
        <div className="absolute right-[-120px] top-60 h-80 w-80 rounded-full bg-orange-500/15 blur-[110px]" />
      </div>

      {/* Hero */}
      <section className="container-custom pb-12 pt-14 md:pt-20">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-500/10 px-3 py-1 text-xs font-medium text-orange-500 dark:text-orange-300">
            <span className="h-1.5 w-1.5 rounded-full bg-orange-500" /> Our product
          </span>
          <h1 className="mt-5 text-5xl font-bold tracking-tight md:text-7xl">
            HR <span className="bg-gradient-to-r from-orange-400 via-rose-400 to-cyan-400 bg-clip-text text-transparent">FLARE</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-600 dark:text-slate-300 md:text-xl">
            One connected place to run Core HR, recruiting, performance and payroll, with an AI copilot that answers, drafts and acts for you. Built by our team.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="rounded-full shadow-lg shadow-primary/25">
              <a href={HRFLARE} target="_blank" rel="noopener noreferrer">
                Visit hrflare.com <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full">
              <Link to="/software/ai-workflow-automation">How we build AI workflows</Link>
            </Button>
          </div>
          <ul className="mt-7 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-600 dark:text-slate-400">
            {trust.map(t => (
              <li key={t} className="inline-flex items-center gap-2"><Check size={16} className="text-cyan-500" /> {t}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Product tour */}
      <section className="container-custom pb-20">
        <div className="-mx-4 mb-5 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div role="tablist" aria-label="Product tour" className="mx-auto flex w-max gap-2">
            {tour.map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active === t.key}
                onClick={() => setActive(t.key)}
                className={cn(
                  'whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200',
                  active === t.key
                    ? 'border-cyan-500 bg-cyan-500 text-white shadow-lg shadow-cyan-500/25'
                    : 'border-slate-200 bg-white/70 text-slate-700 hover:border-cyan-500/40 hover:text-cyan-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="relative mx-auto max-w-6xl">
          <div aria-hidden className="absolute -inset-4 -z-10 rounded-[2.5rem] bg-gradient-to-br from-cyan-500/30 via-primary/20 to-orange-500/20 blur-3xl" />
          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200/80 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 border-b border-slate-200/80 bg-slate-50 px-4 py-3 dark:border-white/10 dark:bg-slate-900">
              <span className="h-3 w-3 rounded-full bg-rose-400" />
              <span className="h-3 w-3 rounded-full bg-amber-400" />
              <span className="h-3 w-3 rounded-full bg-emerald-400" />
              <span className="ml-3 truncate rounded-full bg-white px-3 py-1 font-mono text-xs text-slate-500 dark:bg-white/5 dark:text-slate-400">
                hrflare.com / {current.label.toLowerCase()}
              </span>
            </div>
            <div className="relative aspect-[16/10] bg-slate-100 dark:bg-slate-950">
              {/* All screens stay mounted so switching tabs never shows a blank frame. */}
              {tour.map((t, i) => (
                <img
                  key={t.key}
                  src={shot(t.key)}
                  alt={t.alt}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  aria-hidden={active !== t.key}
                  className={cn(
                    'absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-500',
                    active === t.key ? 'opacity-100' : 'opacity-0'
                  )}
                />
              ))}
            </div>
          </div>
          <p className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">{current.alt}</p>
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-slate-200/80 bg-slate-50/60 py-20 dark:border-white/10 dark:bg-white/[0.02]">
        <div className="container-custom">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Everything a small HR team needs</h2>
            <p className="mt-3 text-lg text-slate-600 dark:text-slate-400">
              A complete HR platform, not just the basics, and every screen respects what each person is allowed to see.
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map(({ icon: Icon, title, body, wide }) => (
              <div
                key={title}
                className={cn(
                  'rounded-3xl border border-slate-200/80 bg-white/80 p-6 transition hover:-translate-y-0.5 hover:border-cyan-500/40 dark:border-white/10 dark:bg-white/[0.03]',
                  wide && 'lg:col-span-2'
                )}
              >
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-300">
                  <Icon size={22} />
                </span>
                <h3 className="mt-4 text-lg font-semibold tracking-tight">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3 rounded-3xl border border-slate-200/80 bg-white/80 p-5 text-sm text-slate-600 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400">
            <ShieldCheck className="h-5 w-5 shrink-0 text-cyan-500" />
            Four core roles and 50+ permissions, enforced in the database. The AI only ever sees what the signed-in person can see.
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-custom py-20">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-cyan-500 via-primary to-violet-600 px-6 py-12 text-white shadow-2xl shadow-cyan-500/20 md:px-12">
          <div aria-hidden className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-xl">
              <h2 className="text-3xl font-bold tracking-tight">Try HR FLARE free</h2>
              <p className="mt-2 text-white/85">Free for up to 5 employees. No credit card, no trial clock.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" variant="secondary" className="rounded-full">
                <a href={HRFLARE} target="_blank" rel="noopener noreferrer">
                  Get started <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white hover:text-primary">
                <Link to="/contact">Talk to us <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
